/**
 * Navigation utilities for handling intended URL redirects
 */

import { devLog } from './logger';

/**
 * Stores the intended URL in sessionStorage
 * @param url - The URL the user intended to visit
 */
export const setIntendedUrl = (url: string): void => {
  devLog.debug('🔗 Setting intended URL:', url);
  sessionStorage.setItem('intendedUrl', url);
};

/**
 * Gets the intended URL from sessionStorage
 * @returns The intended URL or null if none stored
 */
export const getIntendedUrl = (): string | null => {
  const url = sessionStorage.getItem('intendedUrl');
  devLog.debug('🔍 Getting intended URL:', url);
  return url;
};

/**
 * Clears the intended URL from sessionStorage
 */
export const clearIntendedUrl = (): void => {
  devLog.debug('🗑️ Clearing intended URL');
  sessionStorage.removeItem('intendedUrl');
};

/**
 * Gets the intended URL and clears it from storage
 * @returns The intended URL or default fallback
 */
export const getAndClearIntendedUrl = (defaultUrl: string = '/'): string => {
  const intendedUrl = getIntendedUrl();
  clearIntendedUrl();
  const redirectUrl = intendedUrl || defaultUrl;
  devLog.debug('🎯 Redirect URL determined:', redirectUrl, '(intended:', intendedUrl, ', default:', defaultUrl, ')');
  return redirectUrl;
};

/**
 * Checks if the current path should trigger storing as intended URL
 * @param pathname - The current pathname
 * @returns true if this path should be stored as intended
 */
export const shouldStoreAsIntendedUrl = (pathname: string): boolean => {
  // Don't store login page or root as intended URLs
  const excludePaths = ['/login', '/'];
  const shouldStore = !excludePaths.includes(pathname);
  devLog.debug('🤔 Should store as intended URL?', pathname, '→', shouldStore);
  return shouldStore;
};
